import { useState, useEffect, useMemo } from 'react';
import { keyframes } from '@emotion/react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  Card,
  CardHeader,
  CardBody,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  SimpleGrid,
  Flex,
  Icon,
  Avatar,
  useToast,
  Tooltip,
  ButtonGroup,
  IconButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Skeleton,
  SkeletonCircle,
  Collapse,
  useBoolean
} from '@chakra-ui/react';
import {
  FiSearch,
  FiFileText,
  FiInfo,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiRefreshCw,
  FiFilter,
  FiTrendingUp,
  FiTrendingDown,
  FiShare2,
  FiClock,
  FiBarChart2,
  FiCheckCircle,
  FiAlertCircle,
  FiShield,
  FiXCircle,
  FiEye,
  FiCopy,
  FiActivity,
  FiTrendingDown as FiTrendingDownAlt
} from 'react-icons/fi';
import { useTradeLogs, useTradeDetails } from '../hooks/useApi';
import { formatDateTime, timeAgo } from '../utils/formatters';

// Consistent color scheme with Performance page
const THEME_COLORS = {
  cardBg: "rgba(15, 27, 14, 0.8)",
  borderColor: "rgba(78, 224, 53, 0.2)",
  inputBg: "rgba(17, 25, 40, 0.8)",
  accentColor: "green.400",
  accentGradient: "linear(to-r, green.400, green.500)",
  statsBg: "rgba(0, 0, 0, 0.3)"
};

 const cardBg = "rgba(15, 27, 14, 0.8)";
  const borderColor = "rgba(78, 224, 53, 0.2)";

// Simple slide in animation only
const slideIn = keyframes`
  from { 
    opacity: 0;
    transform: translateY(20px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

const TradeLogs = () => {
  const toast = useToast();
  
  // State management
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedTradeId, setSelectedTradeId] = useState(null);
  const [showMobileView, setShowMobileView] = useState(window.innerWidth < 768);
  const [showFilters, setShowFilters] = useBoolean(true);
  
  // Drawer control
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // API calls
  const limit = 25;
  const { 
    data: tradesResponse, 
    isLoading, 
    isError, 
    refetch 
  } = useTradeLogs(page, limit, search);
  
  const { 
    data: tradeDetailsResponse, 
    isLoading: isLoadingDetails 
  } = useTradeDetails(selectedTradeId);
  
  // Responsive handling
  useEffect(() => {
    const handleResize = () => {
      setShowMobileView(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Data extraction
  const trades = tradesResponse?.data?.data?.logs || [];
  const pagination = tradesResponse?.data?.data?.pagination || { total: 0, page: 1, pages: 1, completed: 0, error: 0 };
  const tradeDetails = tradeDetailsResponse?.data?.data?.trade || null;
  
  // Calculate statistics (only completed and error)
  const totalTrades = pagination.total || 0;
  const completedCount = pagination.completed || 0;
  const errorCount = pagination.error || 0;
  const successRate = totalTrades > 0 ? Math.round((completedCount / totalTrades) * 100) : 0;
  
  // Search handling
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };
  
  const handleSearchClear = () => {
    setSearch('');
    setPage(1);
  };
  
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    refetch();
  };
  
  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };
  
  const handleNextPage = () => {
    if (page < pagination.pages) {
      setPage(page + 1);
    }
  };
  
  const handlePageSelect = (pageNum) => {
    setPage(pageNum);
  };
  
  // Drawer handlers
  const handleViewDetails = (tradeId) => {
    setSelectedTradeId(tradeId);
    onOpen();
  };
  
  const handleCloseDetails = () => {
    onClose();
    setSelectedTradeId(null);
  };
  
  // Refresh handler
  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'Trade logs have been updated',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };
  
  // Avatar color based on direction
  const getAvatarColor = (direction) => {
    const dir = (direction || '').toLowerCase();
    const isLong = dir === 'long' || dir === 'buy';
    const isShort = dir === 'short' || dir === 'sell';
    
    if (isLong) return 'green.500';
    if (isShort) return 'red.500';
    return 'gray.500'; // Default for unknown directions
  };
  
  // Status helpers - Enhanced with better colors and effects
  const getStatusBadge = (status) => {
    switch (status) {
      case 0:
        return (
          <Badge 
            colorScheme="green" 
            variant="solid" 
            borderRadius="full"
            bg="green.500"
            color="white"
            px={3}
            py={1}
            fontWeight="bold"
          >
            <HStack spacing={1}>
              <Icon as={FiCheckCircle} boxSize={3} />
              <Text fontSize="xs">Completed</Text>
            </HStack>
          </Badge>
        );
      case 3:
        return (
          <Badge 
            colorScheme="red" 
            variant="solid" 
            borderRadius="full"
            bg="red.500"
            color="white"
            px={3}
            py={1}
            fontWeight="bold"
          >
            <HStack spacing={1}>
              <Icon as={FiXCircle} boxSize={3} />
              <Text fontSize="xs">Failed</Text>
            </HStack>
          </Badge>
        );
      case 4:
        return (
          <Badge 
            colorScheme="yellow" 
            variant="solid" 
            borderRadius="full"
            bg="yellow.500"
            color="white"
            px={3}
            py={1}
            fontWeight="bold"
          >
            <HStack spacing={1}>
              <Icon as={FiShield} boxSize={3} />
              <Text fontSize="xs">Airbag</Text>
            </HStack>
          </Badge>
        );
      default:
        return (
          <Badge 
            colorScheme="orange" 
            variant="solid" 
            borderRadius="full"
            bg="orange.500"
            color="white"
            px={3}
            py={1}
            fontWeight="bold"
          >
            <HStack spacing={1}>
              <Icon as={FiClock} boxSize={3} />
              <Text fontSize="xs">Pending</Text>
            </HStack>
          </Badge>
        );
    }
  };
  
  const getDirectionBadge = (direction) => {
    const isLong = direction?.toLowerCase() === 'long' || direction?.toLowerCase() === 'buy';
    return (
      <Badge 
        colorScheme={isLong ? 'green' : 'red'} 
        variant="solid"
        borderRadius="full"
        bg={isLong ? 'green.500' : 'red.500'}
        color="white"
        px={3}
        py={1}
        fontWeight="bold"
      >
        <HStack spacing={1}>
          <Icon as={isLong ? FiTrendingUp : FiTrendingDown} boxSize={3} />
          <Text fontSize="xs" fontWeight="bold">{isLong ? 'LONG' : 'SHORT'}</Text>
        </HStack>
      </Badge>
    );
  };
  
  // Price formatting helper
  const formatPrice = (price) => {
    const numPrice = parseFloat(price);
    if (isNaN(numPrice) || numPrice === 0) return '-';
    
    if (numPrice < 0.01) return numPrice.toFixed(8);
    if (numPrice < 1) return numPrice.toFixed(6);
    if (numPrice < 1000) return numPrice.toFixed(4);
    return numPrice.toFixed(2);
  };
  
  // Share functionality (moved to drawer)
  const shareToTelegram = (trade) => {
    if (!trade) return;
    
    const symbol = trade.symbol?.toUpperCase();
    const direction = trade.direction?.toLowerCase() === 'long' ? 'Long ▲' : 'Short ▼';
    const entryPrice = formatPrice(trade.entry_price);
    const takeProfit = formatPrice(trade.take_profit);
    const stopLoss = formatPrice(trade.stoploss);
    
    let message = `💹 *${symbol} ${direction}*\n\n`;
    message += `Entry: ${entryPrice}\n`;
    message += `Take Profit: ${takeProfit}\n`;
    message += `Stop Loss: ${stopLoss}\n`;
    
    if (trade.orderid) {
      message += `\nOrder ID: \`${trade.orderid}\`\n`;
    }
    
    message += `\n🚀 Via Wells Finance\n${new Date().toLocaleString()}`;
    
    const encodedMessage = encodeURIComponent(message);
    const url = `https://t.me/share/url?url=https://wells.finance/&text=${encodedMessage}`;
    window.open(url, '_blank');
  };
  
  const shareToWhatsApp = (trade) => {
    if (!trade) return;
    
    const symbol = trade.symbol?.toUpperCase();
    const direction = trade.direction?.toLowerCase() === 'long' ? 'Long ▲' : 'Short ▼';
    const entryPrice = formatPrice(trade.entry_price);
    const takeProfit = formatPrice(trade.take_profit);
    const stopLoss = formatPrice(trade.stoploss);
    
    let message = `💹 ${symbol} ${direction}\n\n`;
    message += `Entry: ${entryPrice}\n`;
    message += `Take Profit: ${takeProfit}\n`;
    message += `Stop Loss: ${stopLoss}\n`;
    
    if (trade.orderid) {
      message += `\nOrder ID: ${trade.orderid}\n`;
    }
    
    message += `\n🚀 Via Wells Finance\n${new Date().toLocaleString()}`;
    
    const encodedMessage = encodeURIComponent(message);
    const url = `https://wa.me/?text=${encodedMessage}`;
    window.open(url, '_blank');
  };
  
  // Copy to clipboard
  const copyTradeToClipboard = (trade) => {
    if (!trade) return;
    
    const symbol = trade.symbol?.toUpperCase();
    const direction = trade.direction?.toLowerCase() === 'long' ? 'Long ▲' : 'Short ▼';
    const entryPrice = formatPrice(trade.entry_price);
    const takeProfit = formatPrice(trade.take_profit);
    const stopLoss = formatPrice(trade.stoploss);
    
    const message = `💹 ${symbol} ${direction}\n\nEntry: ${entryPrice}\nTake Profit: ${takeProfit}\nStop Loss: ${stopLoss}\n\n🚀 Via Wells Finance\n${new Date().toLocaleString()}`;
    
    navigator.clipboard.writeText(message).then(() => {
      toast({
        title: 'Copied!',
        description: 'Trade details copied to clipboard',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    });
  };
  
  // Risk/Reward calculations - Fixed version
  const calculateRiskReward = (trade) => {
    const entry = parseFloat(trade.entry_price);
    const tp = parseFloat(trade.take_profit);
    const sl = parseFloat(trade.stoploss);
    
    // More robust null/undefined checks
    if (!entry || !tp || !sl || entry <= 0 || tp <= 0 || sl <= 0) {
      return null;
    }
    
    // More robust direction check
    const direction = (trade.direction || '').toLowerCase();
    const isLong = direction === 'long' || direction === 'buy';
    const isShort = direction === 'short' || direction === 'sell';
    
    if (!isLong && !isShort) {
      return null;
    }
    
    let reward, risk;
    
    if (isLong) {
      reward = tp - entry;
      risk = entry - sl;
    } else {
      reward = entry - tp;
      risk = sl - entry;
    }
    
    if (reward <= 0 || risk <= 0) {
      return null;
    }
    
    const ratio = reward / risk;
    const rewardPercent = (reward / entry) * 100;
    const riskPercent = (risk / entry) * 100;
    
    return {
      ratio: ratio.toFixed(2),
      rewardPercent: Math.abs(rewardPercent).toFixed(2),
      riskPercent: Math.abs(riskPercent).toFixed(2)
    };
  };
  
  // Simple Skeleton Components - No complex animations
  const SkeletonCard = () => (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={THEME_COLORS.borderColor}
      borderRadius="xl"
      overflow="hidden"
      backdropFilter="blur(10px)"
    >
      <CardBody>
        <VStack spacing={4} align="stretch">
          {/* Header */}
          <HStack justify="space-between">
            <HStack spacing={3}>
              <SkeletonCircle size="12" />
              <Box>
                <Skeleton height="4" width="120px" mb={2} />
                <Skeleton height="3" width="80px" />
              </Box>
            </HStack>
            <Skeleton height="6" width="80px" borderRadius="full" />
          </HStack>
          
          {/* Direction Badge */}
          <Skeleton height="6" width="100px" borderRadius="full" />
          
          {/* Details Grid */}
          <SimpleGrid columns={2} spacing={3}>
            {[1, 2, 3, 4].map((i) => (
              <Box key={i} p={3} bg={THEME_COLORS.statsBg} borderRadius="md">
                <Skeleton height="3" width="60px" mb={2} />
                <Skeleton height="4" width="80px" />
              </Box>
            ))}
          </SimpleGrid>
        </VStack>
      </CardBody>
    </Card>
  );

  const SkeletonTableRow = () => (
    <Tr>
      <Td borderColor="rgba(255, 255, 255, 0.1)">
        <HStack spacing={3}>
          <SkeletonCircle size="12" />
          <Box>
            <Skeleton height="4" width="120px" mb={2} />
            <Skeleton height="3" width="60px" />
          </Box>
        </HStack>
      </Td>
      <Td borderColor="rgba(255, 255, 255, 0.1)">
        <Skeleton height="6" width="80px" borderRadius="full" />
      </Td>
      <Td borderColor="rgba(255, 255, 255, 0.1)">
        <Skeleton height="4" width="80px" />
      </Td>
      <Td borderColor="rgba(255, 255, 255, 0.1)">
        <Skeleton height="8" width="80px" borderRadius="md" />
      </Td>
    </Tr>
  );

  const SkeletonStats = () => (
    <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={6}>
      {[1, 2, 3].map((i) => (
        <Card
          key={i}
          bg={THEME_COLORS.cardBg}
          borderWidth="1px"
          borderColor={THEME_COLORS.borderColor}
          borderRadius="xl"
          overflow="hidden"
          backdropFilter="blur(10px)"
        >
          <CardBody>
            <HStack spacing={4}>
              <SkeletonCircle size="14" />
              <Box flex="1">
                <Skeleton height="4" width="100px" mb={2} />
                <Skeleton height="8" width="80px" mb={1} />
                <Skeleton height="3" width="120px" />
              </Box>
            </HStack>
          </CardBody>
        </Card>
      ))}
    </SimpleGrid>
  );

  // Main Loading Component
  const LoadingContent = () => (
    <VStack spacing={8} align="stretch">
      {/* Header skeleton */}
      <Flex justify="space-between" align="center">
        <HStack spacing={3}>
          <SkeletonCircle size="8" />
          <Skeleton height="8" width="200px" />
        </HStack>
        <Skeleton height="10" width="120px" borderRadius="md" />
      </Flex>

      {/* Search skeleton */}
      <Card
        bg={THEME_COLORS.cardBg}
        borderWidth="1px"
        borderColor={THEME_COLORS.borderColor}
        borderRadius="xl"
        overflow="hidden"
        backdropFilter="blur(10px)"
      >
        <CardHeader>
          <HStack>
            <SkeletonCircle size="6" />
            <Skeleton height="6" width="150px" />
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Skeleton height="12" borderRadius="lg" />
            <HStack justify="space-between">
              <Skeleton height="10" width="150px" borderRadius="md" />
              <Skeleton height="10" width="80px" borderRadius="md" />
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Stats skeleton */}
      <SkeletonStats />

      {/* Content skeleton */}
      <Card
        bg={THEME_COLORS.cardBg}
        borderWidth="1px"
        borderColor={THEME_COLORS.borderColor}
        borderRadius="xl"
        overflow="hidden"
        backdropFilter="blur(10px)"
      >
        <CardHeader>
          <HStack justify="space-between">
            <HStack>
              <SkeletonCircle size="6" />
              <Box>
                <Skeleton height="6" width="150px" mb={1} />
                <Skeleton height="4" width="100px" />
              </Box>
            </HStack>
            <Skeleton height="8" width="150px" borderRadius="md" />
          </HStack>
        </CardHeader>
        <CardBody>
          {showMobileView ? (
            <VStack spacing={4} align="stretch">
              {[1, 2, 3, 4, 5].map((i) => (
                <SkeletonCard key={i} />
              ))}
            </VStack>
          ) : (
            <Box overflowX="auto" borderRadius="xl">
              <Table variant="simple" size="md">
                <Thead>
                  <Tr>
                    <Th borderColor="rgba(255, 255, 255, 0.1)">
                      <Skeleton height="4" width="50px" />
                    </Th>
                    <Th borderColor="rgba(255, 255, 255, 0.1)">
                      <Skeleton height="4" width="60px" />
                    </Th>
                    <Th borderColor="rgba(255, 255, 255, 0.1)">
                      <Skeleton height="4" width="50px" />
                    </Th>
                    <Th borderColor="rgba(255, 255, 255, 0.1)">
                      <Skeleton height="4" width="60px" />
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <SkeletonTableRow key={i} />
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </VStack>
  );
  
  // Loading state - Fixed condition: removed page === 1 check
  if (isLoading) {
    return (
      <Box>
        <LoadingContent />
      </Box>
    );
  }
  
  // Error state
  if (isError) {
    return (
      <Box>
        <VStack spacing={6} align="center" mt={20}>
          <Icon as={FiAlertCircle} boxSize={16} color="red.400" />
          <Heading color="red.400">Oops! Something went wrong</Heading>
          <Text color="gray.500" textAlign="center" maxW="md">
            We couldn't load your trade logs. This might be a temporary issue.
          </Text>
          <Button 
            onClick={refetch} 
            leftIcon={<FiRefreshCw />}
            colorScheme="green"
            bgGradient={THEME_COLORS.accentGradient}
          >
            Try Again
          </Button>
        </VStack>
      </Box>
    );
  }
  
  return (
    <Box>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Box>
            <Heading 
              size="lg" 
              display="flex" 
              alignItems="center"
              animation={`${slideIn} 0.6s ease-out`}
            >
              <Icon as={FiFileText} mr={3} color={THEME_COLORS.accentColor} />
              Trade Logs
            </Heading>
          </Box>
          <ButtonGroup spacing={3}>
            <Button
              onClick={handleRefresh}
              leftIcon={<FiRefreshCw />}
              colorScheme="green"
              bgGradient={THEME_COLORS.accentGradient}
              isLoading={isLoading}
            >
              Refresh
            </Button>
          </ButtonGroup>
        </Flex>
        
        {/* Search and Filters */}
        <Collapse in={showFilters} animateOpacity>
          <Card
            bg={THEME_COLORS.cardBg}
            borderWidth="1px"
            borderColor={THEME_COLORS.borderColor}
            shadow="xl"
            borderRadius="xl"
            overflow="hidden"
            backdropFilter="blur(10px)"
          >
            <CardHeader>
              <HStack>
                <Icon as={FiFilter} color={THEME_COLORS.accentColor} />
                <Heading size="md">Search & Filter</Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <form onSubmit={handleSearchSubmit}>
                <VStack spacing={4} align="stretch">
                  <InputGroup size="lg">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiSearch} color="green.300" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search by symbol, signal ID, or order details..."
                      value={search}
                      onChange={handleSearchChange}
                      bg={THEME_COLORS.inputBg}
                      borderColor="rgba(255, 255, 255, 0.16)"
                      color="white"
                      _focus={{
                        borderColor: "green.400",
                        boxShadow: "0 0 0 1px var(--chakra-colors-green-400)"
                      }}
                      _hover={{ borderColor: "green.300" }}
                    />
                    {search && (
                      <InputRightElement>
                        <IconButton
                          aria-label="Clear search"
                          icon={<FiX />}
                          size="sm"
                          variant="ghost"
                          onClick={handleSearchClear}
                          borderRadius="full"
                        />
                      </InputRightElement>
                    )}
                  </InputGroup>
                  
                  <HStack justify="space-between">
                    <Button
                      type="submit"
                      colorScheme="green"
                      bgGradient={THEME_COLORS.accentGradient}
                      leftIcon={<FiSearch />}
                      isLoading={isLoading}
                    >
                      Search Trades
                    </Button>
                    
                    {search && (
                      <Button
                        variant="ghost"
                        leftIcon={<FiX />}
                        onClick={handleSearchClear}
                        color="gray.500"
                      >
                        Clear
                      </Button>
                    )}
                  </HStack>
                </VStack>
              </form>
            </CardBody>
          </Card>
        </Collapse>
        
        {/* Statistics Dashboard - Enhanced with better icons and colors */}
        <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={6}>
          {[
            {
              label: 'Total Trades',
              value: totalTrades,
              icon: FiBarChart2,
              color: 'blue',
              gradient: 'linear(to-br, blue.400, blue.600)',
              iconBg: 'linear(to-br, blue.500, blue.700)',
              shadow: '0 4px 15px rgba(59, 130, 246, 0.2)'
            },
            {
              label: 'Completed',
              value: completedCount,
              icon: FiCheckCircle,
              color: 'green',
              subtitle: `${successRate}% success rate`,
              gradient: 'linear(to-br, green.400, green.600)',
              iconBg: 'linear(to-br, green.500, green.700)',
              shadow: '0 4px 15px rgba(34, 197, 94, 0.2)'
            },
            {
              label: 'Failed',
              value: errorCount,
              icon: FiAlertCircle,
              color: 'red',
              gradient: 'linear(to-br, red.400, red.600)',
              iconBg: 'linear(to-br, red.500, red.700)',
              shadow: '0 4px 15px rgba(239, 68, 68, 0.2)'
            }
          ].map((stat, index) => (
            <Card
              key={stat.label}
              bg={THEME_COLORS.cardBg}
              borderWidth="1px"
              borderColor={THEME_COLORS.borderColor}
              borderRadius="xl"
              overflow="hidden"
              backdropFilter="blur(10px)"
              animation={`${slideIn} 0.6s ease-out ${index * 0.1}s both`}
              _hover={{ 
                transform: 'translateY(-5px)',
                shadow: stat.shadow,
                borderColor: `${stat.color}.400`
              }}
              transition="all 0.3s"
              cursor="pointer"
            >
              <CardBody>
                <HStack spacing={4}>
                  <Box
                    p={3}
                    borderRadius="xl"
                    bgGradient={stat.iconBg}
                    color="white"
                    shadow="lg"
                    _hover={{ transform: 'scale(1.1)' }}
                    transition="transform 0.2s"
                  >
                    <Icon as={stat.icon} boxSize={7} />
                  </Box>
                  <Box flex="1">
                    <Text fontSize="sm" color="gray.400" fontWeight="medium" mb={1}>
                      {stat.label}
                    </Text>
                    <Text fontSize="3xl" fontWeight="bold" color="white" lineHeight="1">
                      {stat.value.toLocaleString()}
                    </Text>
                    {stat.subtitle && (
                      <Text fontSize="xs" color={`${stat.color}.400`} fontWeight="medium" mt={1}>
                        {stat.subtitle}
                      </Text>
                    )}
                  </Box>
                </HStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
        
        {/* Trade Logs */}
        <Card
          bg={THEME_COLORS.cardBg}
          borderWidth="1px"
          borderColor={THEME_COLORS.borderColor}
          shadow="xl"
          borderRadius="xl"
          overflow="hidden"
          backdropFilter="blur(10px)"
        >
          <CardHeader>
            <HStack justify="space-between">
              <HStack>
                <Icon as={FiActivity} color={THEME_COLORS.accentColor} />
                <Box>
                  <Heading size="md">Recent Trades</Heading>
                  <Text fontSize="sm" color="gray.500">
                    {pagination.total} trades found
                  </Text>
                </Box>
              </HStack>
              
              {/* View Toggle */}
              <ButtonGroup variant="outline" isAttached size="sm">
                <Button 
                  isActive={!showMobileView}
                  onClick={() => setShowMobileView(false)}
                  leftIcon={<FiBarChart2 />}
                >
                  Table
                </Button>
                <Button 
                  isActive={showMobileView}
                  onClick={() => setShowMobileView(true)}
                  leftIcon={<FiFileText />}
                >
                  Cards
                </Button>
              </ButtonGroup>
            </HStack>
          </CardHeader>
          <CardBody>
            {trades.length === 0 ? (
              <VStack spacing={6} py={16}>
                <Icon as={FiFileText} boxSize={20} color="gray.300" />
                <VStack spacing={2}>
                  <Heading size="lg" color="gray.400">
                    {search ? 'No matches found' : 'No trades yet'}
                  </Heading>
                  <Text color="gray.500" textAlign="center" maxW="md">
                    {search 
                      ? `No trades match "${search}". Try adjusting your search terms.`
                      : 'Your trading activity will appear here. Start trading to see your first entries!'
                    }
                  </Text>
                </VStack>
                {search && (
                  <Button
                    onClick={handleSearchClear}
                    leftIcon={<FiX />}
                    colorScheme="blue"
                    variant="outline"
                  >
                    Clear Search
                  </Button>
                )}
              </VStack>
            ) : showMobileView ? (
              // Mobile Cards View
              <VStack spacing={4} align="stretch">
                {trades.map((trade, index) => (
                  <Card
                    key={trade.id}
                    variant="outline"
                    borderWidth="1px"
                    borderColor={THEME_COLORS.borderColor}
                    borderRadius="xl"
                    _hover={{ 
                      borderColor: 'green.300',
                      shadow: 'lg',
                      transform: 'translateY(-2px)'
                    }}
                    transition="all 0.3s"
                    cursor="pointer"
                    onClick={() => handleViewDetails(trade.id)}
                    animation={`${slideIn} 0.5s ease-out ${index * 0.1}s both`}
                    bg={THEME_COLORS.cardBg}
                    backdropFilter="blur(10px)"
                  >
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        {/* Header */}
                        <HStack justify="space-between">
                          <HStack spacing={3}>
                            <Avatar
                              size="md"
                              name={trade.symbol?.charAt(0).toUpperCase()}
                              bg={getAvatarColor(trade.direction)}
                              color="white"
                            />
                            <Box>
                              <Text fontWeight="bold" fontSize="lg" color="white">
                                {trade.symbol?.toUpperCase()}
                              </Text>
                              <Text fontSize="sm" color="gray.400">
                               {timeAgo(trade.created_at)}
                              </Text>
                            </Box>
                          </HStack>
                      
                        </HStack>
                        
                        {/* Direction Badge */}
                    <HStack spacing={4} width="100%" justifyContent="space-between">
  {getDirectionBadge(trade.direction)}
  {getStatusBadge(trade.waiting)}
</HStack>
                        
                        {/* Trade Details Grid */}
                        <SimpleGrid columns={2} spacing={3}>
                          <Box p={3} bg={cardBg} borderRadius="md">
                            <Text fontSize="xs" color="gray.400">Entry Price</Text>
                            <Text fontSize="md" fontWeight="bold" color="white">
                              {formatPrice(trade.entry_price)}
                            </Text>
                          </Box>
                          <Box p={3} bg={cardBg} borderRadius="md">
                            <Text fontSize="xs" color="gray.400">Take Profit</Text>
                            <Text fontSize="md" fontWeight="bold" color="green.400">
                              {formatPrice(trade.take_profit)}
                            </Text>
                          </Box>
                          <Box p={3} bg={cardBg} borderRadius="md">
                            <Text fontSize="xs" color="gray.400">Stop Loss</Text>
                            <Text fontSize="md" fontWeight="bold" color="red.400">
                              {formatPrice(trade.stoploss)}
                            </Text>
                          </Box>
                          <Box p={3} bg={cardBg} borderRadius="md">
                            <Text fontSize="xs" color="gray.400">Risk/Reward</Text>
                            <Text fontSize="md" fontWeight="bold" color="blue.400">
                              {(() => {
                                const rr = calculateRiskReward(trade);
                                return rr ? `${rr.ratio}:1` : '-';
                              })()}
                            </Text>
                          </Box>
                        </SimpleGrid>
                      </VStack>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            ) : (
              // Simplified Desktop Table View - Only 4 columns
              <Box overflowX="auto" borderRadius="xl">
                <Table variant="simple" size="md">
                  <Thead>
                    <Tr>
                      <Th borderColor="rgba(255, 255, 255, 0.1)" color="gray.300">Trade</Th>
                      <Th borderColor="rgba(255, 255, 255, 0.1)" color="gray.300">Status</Th>
                      <Th borderColor="rgba(255, 255, 255, 0.1)" color="gray.300">Time</Th>
                      <Th borderColor="rgba(255, 255, 255, 0.1)" color="gray.300">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {trades.map((trade, index) => (
                      <Tr 
                        key={trade.id} 
                        _hover={{ bg: "rgba(255, 255, 255, 0.05)" }}
                        transition="all 0.2s"
                        animation={`${slideIn} 0.4s ease-out ${index * 0.05}s both`}
                      >
                        <Td borderColor="rgba(255, 255, 255, 0.1)">
                          <HStack spacing={3}>
                            <Avatar
                              size="md"
                              name={trade.symbol?.charAt(0).toUpperCase()}
                              bg={getAvatarColor(trade.direction)}
                              color="white"
                            />
                            <Box>
                              <Text fontWeight="bold" fontSize="md" color="white">
                                {trade.symbol?.toUpperCase()}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {trade.signal_id || 'Manual'}
                              </Text>
                            </Box>
                          </HStack>
                        </Td>
                        <Td borderColor="rgba(255, 255, 255, 0.1)">
                          {getStatusBadge(trade.waiting)}
                        </Td>
                        <Td borderColor="rgba(255, 255, 255, 0.1)">
                          <Tooltip label={formatDateTime(trade.created_at)} hasArrow>
                            <Text fontSize="sm" cursor="help" color="white">
                              {timeAgo(trade.created_at)}
                            </Text>
                          </Tooltip>
                        </Td>
                        <Td borderColor="rgba(255, 255, 255, 0.1)">
                          <Button
                            size="sm"
                            colorScheme="green"
                            variant="ghost"
                            leftIcon={<FiEye />}
                            onClick={() => handleViewDetails(trade.id)}
                          >
                            Details
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
            
            {/* Pagination */}
            {pagination.pages > 1 && (
              <Flex justify="center" mt={8}>
                <HStack spacing={2}>
                  <IconButton
                    aria-label="Previous page"
                    icon={<FiChevronLeft />}
                    onClick={handlePrevPage}
                    isDisabled={page <= 1}
                    variant="outline"
                    colorScheme="green"
                  />
                  
                  {/* Page numbers */}
                  {(() => {
                    const startPage = Math.max(1, page - 2);
                    const endPage = Math.min(pagination.pages, startPage + 4);
                    const pages = [];
                    
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <Button
                          key={i}
                          onClick={() => handlePageSelect(i)}
                          variant={i === page ? 'solid' : 'outline'}
                          colorScheme={i === page ? 'green' : 'gray'}
                          size="sm"
                          bgGradient={i === page ? THEME_COLORS.accentGradient : undefined}
                        >
                          {i}
                        </Button>
                      );
                    }
                    
                    return pages;
                  })()}
                  
                  <IconButton
                    aria-label="Next page"
                    icon={<FiChevronRight />}
                    onClick={handleNextPage}
                    isDisabled={page >= pagination.pages}
                    variant="outline"
                    colorScheme="green"
                  />
                </HStack>
              </Flex>
            )}
          </CardBody>
        </Card>
      </VStack>
      
      {/* Enhanced Trade Details Drawer */}
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleCloseDetails}
        size={{ base: "full", md: "lg" }}
      >
        <DrawerOverlay bg="blackAlpha.800" backdropFilter="blur(4px)" />
        <DrawerContent 
          bg="rgba(15, 27, 14, 0.8)" 
          borderLeftRadius="xl" 
          shadow="2xl"
          borderLeftWidth="1px"
          borderLeftColor="green.500"
        >
          <DrawerCloseButton 
            size="lg" 
            color="white" 
            _hover={{ bg: "whiteAlpha.200" }}
            borderRadius="full"
          />
          
          <DrawerHeader 
            borderBottomWidth="1px" 
            borderBottomColor="whiteAlpha.200"
            bg="rgba(15, 27, 14, 0.8)"
            py={{ base: 3, md: 4 }}
            px={{ base: 4, md: 6 }}
          >
            <HStack spacing={3}>
              <Icon as={FiInfo} color="green.400" boxSize={{ base: 5, md: 6 }} />
              <Box>
                <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="white">
                  Trade Details
                </Text>
                {tradeDetails && (
                  <Flex direction={{ base: "column", sm: "row" }} gap={2} mt={1}>
                    <Badge colorScheme="blue" size="sm" borderRadius="md" fontSize="xs">
                      ID: {tradeDetails.id}
                    </Badge>
                    {tradeDetails.orderid && (
                      <Badge colorScheme="green" size="sm" borderRadius="md" fontSize="xs">
                        Order: {tradeDetails.orderid}
                      </Badge>
                    )}
                  </Flex>
                )}
              </Box>
            </HStack>
          </DrawerHeader>
          
          <DrawerBody bg="rgba(15, 27, 14, 0.8)" p={{ base: 4, md: 6 }}>
            {isLoadingDetails ? (
              <VStack spacing={6} align="stretch" mt={0}>
                <Skeleton height="150px" borderRadius="xl" />
                <SimpleGrid columns={2} spacing={4}>
                  <Skeleton height="80px" borderRadius="xl" />
                  <Skeleton height="80px" borderRadius="xl" />
                  <Skeleton height="80px" borderRadius="xl" />
                  <Skeleton height="80px" borderRadius="xl" />
                </SimpleGrid>
              </VStack>
            ) : tradeDetails ? (
              <VStack spacing={6} align="stretch" mt={0}>
                {/* Status Alert */}
                {tradeDetails.waiting === 3 && (
                  <Alert status="error" borderRadius="lg" bg="red.900" border="1px solid" borderColor="red.500">
                    <AlertIcon />
                    <Box>
                      <AlertTitle color="red.200">Trade Failed</AlertTitle>
                      <AlertDescription color="red.300">
                        {tradeDetails.error_text || 'Unknown error occurred while processing the trade.'}
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}
                
                {tradeDetails.waiting === 0 && (
                  <Alert status="success" borderRadius="lg" bg="green.900" border="1px solid" borderColor="green.500">
                    <AlertIcon />
                    <Box>
                      <AlertTitle color="green.200">Trade Completed</AlertTitle>
                      <AlertDescription color="green.300">
                        Order successfully executed
                        {tradeDetails.orderid && ` with ID: ${tradeDetails.orderid}`}
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}
                
                {/* Trade Overview */}
                <Box 
                  p={{ base: 4, md: 6 }} 
                  bg="rgba(15, 27, 14, 0.8)" 
                  borderRadius="xl" 
                  border="1px solid" 
                  borderColor="whiteAlpha.200"
                >
                  <HStack spacing={4} mb={{ base: 4, md: 6 }}>
                    <Avatar
                      size={{ base: "md", md: "lg" }}
                      name={tradeDetails.symbol?.charAt(0).toUpperCase()}
                      bg={getAvatarColor(tradeDetails.direction)}
                      color="white"
                    />
                    <Box flex="1">
                      <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="white" mb={2}>
                        {tradeDetails.symbol?.toUpperCase()}
                      </Text>
                   
                    </Box>
                  </HStack>
                  
                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                    <Box p={3} bg="whiteAlpha.100" borderRadius="lg">
                      <Text fontSize="sm" color="gray.400" mb={1}>Entry Price</Text>
                      <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="white">
                        {formatPrice(tradeDetails.entry_price)}
                      </Text>
                    </Box>
                    
                    <Box p={3} bg="green.900" borderRadius="lg">
                      <Text fontSize="sm" color="green.400" mb={1}>Take Profit</Text>
                      <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="green.400">
                        {formatPrice(tradeDetails.take_profit)}
                      </Text>
                    </Box>
                    
                    <Box p={3} bg="red.900" borderRadius="lg">
                      <Text fontSize="sm" color="red.400" mb={1}>Stop Loss</Text>
                      <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="red.400">
                        {formatPrice(tradeDetails.stoploss)}
                      </Text>
                    </Box>
                    
                    <Box p={3} bg="blue.900" borderRadius="lg">
                      <Text fontSize="sm" color="blue.400" mb={1}>Status</Text>
                      <Box mt={2}>
                        {getStatusBadge(tradeDetails.waiting)}
                      </Box>
                    </Box>
                  </SimpleGrid>
                </Box>

                {tradeDetails.waiting === 4 && (
  <Box 
    p={{ base: 3, md: 4 }} 
    bg="yellow.900" 
    borderRadius="xl" 
    border="1px solid" 
    borderColor="yellow.400"
  >
    <HStack spacing={3} mb={2}>
      <Icon as={FiShield} color="yellow.400" boxSize={5} />
      <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="yellow.200">
        Airbag Protection Details
      </Text>
    </HStack>
    <Text fontSize="sm" color="yellow.300">
      This trade was automatically blocked by the airbag protection system. 
      The system detected conditions that could lead to excessive risk and prevented the trade execution to protect your capital.
    </Text>
    <Text fontSize="xs" color="yellow.400" mt={2} fontWeight="bold">
      ✓ Protection system working correctly - No action required
    </Text>
  </Box>
)}
                
                {/* Risk Analysis */}
                {(() => {
                  const riskReward = calculateRiskReward(tradeDetails);
                  if (!riskReward) return null;
                  
                  return (
                    <Box 
                      p={{ base: 3, md: 4 }} 
                      bg="rgba(15, 27, 14, 0.8)" 
                      borderRadius="xl" 
                      border="1px solid" 
                      borderColor="whiteAlpha.200"
                    >
                      <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="white" mb={{ base: 3, md: 4 }}>Risk Analysis</Text>
                      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
                        <Box textAlign="center" p={3} bg="purple.900" borderRadius="lg">
                          <Text fontSize="xs" color="purple.400" mb={1}>Risk/Reward</Text>
                          <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="purple.400">
                            {riskReward.ratio}:1
                          </Text>
                        </Box>
                        <Box textAlign="center" p={3} bg="green.900" borderRadius="lg">
                          <Text fontSize="xs" color="green.400" mb={1}>Potential Gain</Text>
                          <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="green.400">
                            +{riskReward.rewardPercent}%
                          </Text>
                        </Box>
                        <Box textAlign="center" p={3} bg="red.900" borderRadius="lg">
                          <Text fontSize="xs" color="red.400" mb={1}>Potential Loss</Text>
                          <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="red.400">
                            -{riskReward.riskPercent}%
                          </Text>
                        </Box>
                      </SimpleGrid>
                    </Box>
                  );
                })()}
                
                {/* Timeline */}
                <Box 
                  p={{ base: 3, md: 4 }} 
                  bg="rgba(15, 27, 14, 0.8)" 
                  borderRadius="xl" 
                  border="1px solid" 
                  borderColor="whiteAlpha.200"
                >
                  <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="white" mb={{ base: 3, md: 4 }}>Timeline</Text>
                  <VStack spacing={3} align="stretch">
                    <HStack p={3} bg="green.900" borderRadius="lg">
                      <Icon as={FiClock} color="green.400" />
                      <Box flex="1">
                        <Text fontWeight="bold" color="green.400" fontSize={{ base: "sm", md: "md" }}>Trade Created</Text>
                        <Text fontSize="xs" color="gray.400">
                          {formatDateTime(tradeDetails.created_at)} · {timeAgo(tradeDetails.created_at)}
                        </Text>
                      </Box>
                    </HStack>
                    
                    {tradeDetails.updated_at !== tradeDetails.created_at && (
                      <HStack p={3} bg="blue.900" borderRadius="lg">
                        <Icon as={FiActivity} color="blue.400" />
                        <Box flex="1">
                          <Text fontWeight="bold" color="blue.400" fontSize={{ base: "sm", md: "md" }}>Last Updated</Text>
                          <Text fontSize="xs" color="gray.400">
                            {formatDateTime(tradeDetails.updated_at)} · {timeAgo(tradeDetails.updated_at)}
                          </Text>
                        </Box>
                      </HStack>
                    )}
                  </VStack>
                </Box>
                
                {/* Model Information */}
                {tradeDetails.model && (
                  <Box 
                    p={{ base: 3, md: 4 }} 
                    bg="rgba(15, 27, 14, 0.8)" 
                    borderRadius="xl" 
                    border="1px solid" 
                    borderColor="whiteAlpha.200"
                  >
                    <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="white" mb={{ base: 3, md: 4 }}>AI Model Analysis</Text>
                    <Box
                      p={{ base: 3, md: 4 }}
                      bg="rgba(15, 27, 14, 0.8)"
                      color="green.400"
                      borderRadius="lg"
                      fontFamily="mono"
                      fontSize={{ base: "xs", md: "sm" }}
                      whiteSpace="pre-wrap"
                      overflowX="auto"
                      maxH={{ base: "150px", md: "200px" }}
                      overflowY="auto"
                      border="1px solid"
                      borderColor="whiteAlpha.200"
                    >
                      {tradeDetails.model}
                    </Box>
                  </Box>
                )}
              </VStack>
            ) : (
              <Alert status="error" borderRadius="lg" bg="red.900" border="1px solid" borderColor="red.500">
                <AlertIcon />
                <Box>
                  <AlertTitle color="red.200">Failed to load details</AlertTitle>
                  <AlertDescription color="red.300">
                    There was an error loading the trade details. Please try again.
                  </AlertDescription>
                </Box>
              </Alert>
            )}
          </DrawerBody>
          
          <DrawerFooter 
            borderTopWidth="1px" 
            borderTopColor="whiteAlpha.200"
            bg="rgba(15, 27, 14, 0.8)"
            p={{ base: 3, md: 6 }}
          >
            {tradeDetails && (
              <Flex 
                direction={{ base: "column", sm: "row" }} 
                spacing={2} 
                gap={2}
                width="100%"
              >
                <Button
                  onClick={() => copyTradeToClipboard(tradeDetails)}
                  leftIcon={<FiCopy />}
                  variant="outline"
                  colorScheme="blue"
                  size={{ base: "sm", md: "md" }}
                  flex="1"
                  bg="whiteAlpha.100"
                  _hover={{ bg: "whiteAlpha.200" }}
                  fontSize={{ base: "xs", md: "sm" }}
                  minH={{ base: "36px", md: "40px" }}
                >
                  Copy
                </Button>
                <Button
                  onClick={() => shareToTelegram(tradeDetails)}
                  leftIcon={<FiShare2 />}
                  colorScheme="blue"
                  size={{ base: "sm", md: "md" }}
                  flex="1"
                  bg="blue.600"
                  _hover={{ bg: "blue.700" }}
                  fontSize={{ base: "xs", md: "sm" }}
                  minH={{ base: "36px", md: "40px" }}
                >
                  Telegram
                </Button>
                <Button
                  onClick={() => shareToWhatsApp(tradeDetails)}
                  leftIcon={<FiShare2 />}
                  colorScheme="green"
                  size={{ base: "sm", md: "md" }}
                  flex="1"
                  bg="green.600"
                  _hover={{ bg: "green.700" }}
                  fontSize={{ base: "xs", md: "sm" }}
                  minH={{ base: "36px", md: "40px" }}
                >
                  WhatsApp
                </Button>
              </Flex>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default TradeLogs;